/**
 * Dev Logs Dashboard
 * 
 * UI for viewing and analyzing development logs
 * Only accessible to jp@naible.ai
 */

import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Spinner } from '@/components/ui/spinner'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { 
  AlertCircle, 
  Bug, 
  Info, 
  AlertTriangle, 
  XCircle, 
  Activity,
  Trash2,
  RefreshCw,
  Download,
  Filter
} from 'lucide-react'
import { getDevLogs, getDevLogStats, type DevLogEntry } from '@/services/devLogService'
import { cn } from '@/lib/utils'

const severityConfig = {
  debug: { icon: Info, color: 'text-gray-500', bg: 'bg-gray-100', label: 'Debug' },
  info: { icon: Info, color: 'text-blue-500', bg: 'bg-blue-100', label: 'Info' },
  warning: { icon: AlertTriangle, color: 'text-yellow-500', bg: 'bg-yellow-100', label: 'Warning' },
  error: { icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-100', label: 'Error' },
  critical: { icon: XCircle, color: 'text-purple-500', bg: 'bg-purple-100', label: 'Critical' }
}

const logTypeConfig = {
  json_parse_error: { icon: Bug, label: 'JSON Parse Error' },
  api_error: { icon: AlertCircle, label: 'API Error' },
  validation_error: { icon: AlertTriangle, label: 'Validation Error' },
  runtime_error: { icon: XCircle, label: 'Runtime Error' },
  custom: { icon: Activity, label: 'Custom' }
}

export default function DevLogsDashboard() {
  const [logs, setLogs] = useState<DevLogEntry[]>([])
  const [stats, setStats] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedLog, setSelectedLog] = useState<DevLogEntry | null>(null)
  const [filterSeverity, setFilterSeverity] = useState<string>('all')
  const [filterLogType, setFilterLogType] = useState<string>('all')

  const loadData = async () => {
    setLoading(true)
    setError(null)
    
    try {
      const [logsData, statsData] = await Promise.all([
        getDevLogs({
          severity: filterSeverity !== 'all' ? filterSeverity : undefined,
          logType: filterLogType !== 'all' ? filterLogType : undefined,
          limit: 500
        }),
        getDevLogStats()
      ])
      
      setLogs(logsData)
      setStats(statsData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load logs')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [filterSeverity, filterLogType])

  const exportLogs = () => {
    const dataStr = JSON.stringify(logs, null, 2)
    const dataBlob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(dataBlob)
    const link = document.createElement('a')
    link.href = url
    link.download = `dev-logs-${new Date().toISOString()}.json`
    link.click()
    URL.revokeObjectURL(url)
  }

  if (loading && !logs.length) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Spinner className="mx-auto mb-4" />
          <p className="text-muted-foreground">Loading dev logs...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Development Logs</h1>
          <p className="text-muted-foreground">System-wide error tracking and debugging</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={exportLogs} variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Logs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Last 24 Hours</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.recent24h}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Error Rate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.total > 0 
                  ? `${Math.round((stats.bySeverity.error || 0) / stats.total * 100)}%`
                  : '0%'
                }
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Most Common</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm font-medium">
                {Object.entries(stats.byType).sort((a: any, b: any) => b[1] - a[1])[0]?.[0] || 'N/A'}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Severity</label>
              <Select value={filterSeverity} onValueChange={setFilterSeverity}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Severities</SelectItem>
                  <SelectItem value="debug">Debug</SelectItem>
                  <SelectItem value="info">Info</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="error">Error</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Log Type</label>
              <Select value={filterLogType} onValueChange={setFilterLogType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="json_parse_error">JSON Parse Error</SelectItem>
                  <SelectItem value="api_error">API Error</SelectItem>
                  <SelectItem value="validation_error">Validation Error</SelectItem>
                  <SelectItem value="runtime_error">Runtime Error</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Display */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Log List */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Recent Logs ({logs.length})</CardTitle>
            <CardDescription>Click on a log to view details</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[600px]">
              <div className="space-y-2">
                {logs.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No logs found
                  </div>
                ) : (
                  logs.map((log) => {
                    const SeverityIcon = severityConfig[log.severity]?.icon || Info
                    const LogTypeIcon = logTypeConfig[log.logType]?.icon || Activity
                    
                    return (
                      <div
                        key={log.id}
                        onClick={() => setSelectedLog(log)}
                        className={cn(
                          'p-3 rounded-lg border cursor-pointer transition-colors hover:bg-accent',
                          selectedLog?.id === log.id && 'bg-accent border-primary'
                        )}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            <SeverityIcon className={cn('w-4 h-4', severityConfig[log.severity]?.color)} />
                            <Badge variant="outline" className="text-xs">
                              {logTypeConfig[log.logType]?.label || log.logType}
                            </Badge>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {new Date(log.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm font-medium line-clamp-2">{log.message}</p>
                        {log.context && (
                          <p className="text-xs text-muted-foreground mt-1">{log.context}</p>
                        )}
                      </div>
                    )
                  })
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Log Details */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Log Details</CardTitle>
            <CardDescription>Detailed information about selected log</CardDescription>
          </CardHeader>
          <CardContent>
            {!selectedLog ? (
              <div className="text-center py-8 text-muted-foreground">
                Select a log to view details
              </div>
            ) : (
              <ScrollArea className="h-[600px]">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">ID</label>
                    <p className="text-sm text-muted-foreground font-mono">{selectedLog.id}</p>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">Message</label>
                    <p className="text-sm mt-1">{selectedLog.message}</p>
                  </div>

                  {selectedLog.context && (
                    <div>
                      <label className="text-sm font-medium">Context</label>
                      <p className="text-sm text-muted-foreground mt-1">{selectedLog.context}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Severity</label>
                      <Badge className="mt-1" variant="outline">
                        {severityConfig[selectedLog.severity]?.label || selectedLog.severity}
                      </Badge>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Type</label>
                      <Badge className="mt-1" variant="outline">
                        {logTypeConfig[selectedLog.logType]?.label || selectedLog.logType}
                      </Badge>
                    </div>
                  </div>

                  {selectedLog.source && (
                    <div>
                      <label className="text-sm font-medium">Source</label>
                      <p className="text-sm text-muted-foreground mt-1">{selectedLog.source}</p>
                    </div>
                  )}

                  {selectedLog.inputData && (
                    <div>
                      <label className="text-sm font-medium">Input Data</label>
                      <pre className="text-xs bg-muted p-2 rounded mt-1 overflow-x-auto">
                        {selectedLog.inputData}
                      </pre>
                    </div>
                  )}

                  {selectedLog.stackTrace && (
                    <div>
                      <label className="text-sm font-medium">Stack Trace</label>
                      <pre className="text-xs bg-muted p-2 rounded mt-1 overflow-x-auto whitespace-pre-wrap">
                        {selectedLog.stackTrace}
                      </pre>
                    </div>
                  )}

                  {selectedLog.metadata && (
                    <div>
                      <label className="text-sm font-medium">Metadata</label>
                      <pre className="text-xs bg-muted p-2 rounded mt-1 overflow-x-auto">
                        {JSON.stringify(selectedLog.metadata, null, 2)}
                      </pre>
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-medium">Timestamp</label>
                    <p className="text-sm text-muted-foreground mt-1">
                      {new Date(selectedLog.timestamp).toLocaleString()}
                    </p>
                  </div>

                  {selectedLog.userAgent && (
                    <div>
                      <label className="text-sm font-medium">User Agent</label>
                      <p className="text-xs text-muted-foreground mt-1 break-all">
                        {selectedLog.userAgent}
                      </p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
